import { c as _c } from "react/compiler-runtime";
import { Api, ApiContext, ApiProvider, createApi, getBaseUrl } from '@netpurpose/api';
import { AlertBannerContext, AlertBannerProvider, defaultAlertBannerContext, type IAlertBannerContext, ModalProvider, useLoggedIn, usePostHogUser, useSentryUser } from '@netpurpose/core';
import { AntdThemeProvider, GlobalStyles, lightTheme } from '@netpurpose/np-ui';
import { useUnleashContext } from '@unleash/proxy-client-react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { type Context, type FC, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import type { AlertBannerType } from '#components/common/AlertBanner';
import GlobalErrorBanner from '#components/common/GlobalErrorBanner';
import { AuthContext, AuthProvider, defaultContext as defaultAuthContext, type IAuthContext } from '#context/auth';
import { defaultContext as defaultTaskContext, type ITaskContext, TaskContext, TaskProvider } from '#context/tasks';
import { config } from '#services/config';
import { AnalyticsContext, type AnalyticsContextType, AnalyticsProvider, defaultContext as defaultAnalyticsContext } from './analytics';
import { QueryClientProvider } from './queryClient';
import { UnleashProvider } from './UnleashProvider';
const TypedAlertBannerContext = AlertBannerContext as Context<IAlertBannerContext<AlertBannerType>>;
type Props = {
  children: React.ReactNode;
};
const UpdateUnleashContext: FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(7);
  const {
    children
  } = t0;
  const {
    user: loggedInUser
  } = useLoggedIn();
  const updateUnleashContext = useUnleashContext();
  const userGroup = loggedInUser?.groups[0]?.groupId;
  let t1;
  if ($[0] !== updateUnleashContext || $[1] !== userGroup) {
    t1 = () => {
      updateUnleashContext({
        ...(userGroup ? {
          userId: userGroup
        } : {})
      });
    };
    $[0] = updateUnleashContext;
    $[1] = userGroup;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== userGroup) {
    t2 = [userGroup];
    $[3] = userGroup;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== children) {
    t3 = <>{children}</>;
    $[5] = children;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};

// Sentry stays the source of truth for user attribution. usePostHogUser runs in
// parallel so PostHog gets the same identity for comparison.
const UserSync: FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  useSentryUser();
  usePostHogUser();
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const ContextProvider: FC<Props> = t0 => {
  const $ = _c(4);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      axios: {
        getAxiosInstance: createApi,
        factsServiceBaseUrl: getBaseUrl(config.factsHost),
        modellingServiceBaseUrl: getBaseUrl(config.modellingHost),
        usersServiceBaseUrl: getBaseUrl(config.usersHost),
        aiServiceBaseUrl: getBaseUrl(config.aiHost)
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <GlobalStyles />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <UnleashProvider><PostHogProvider client={posthog}><AuthProvider><QueryClientProvider><ApiProvider apiConfig={t1}><UserSync><AnalyticsProvider><UpdateUnleashContext><AntdThemeProvider isClientApp={true}><ThemeProvider theme={lightTheme}>{t2}<AlertBannerProvider><GlobalErrorBanner><TaskProvider><ModalProvider>{children}</ModalProvider></TaskProvider></GlobalErrorBanner></AlertBannerProvider></ThemeProvider></AntdThemeProvider></UpdateUnleashContext></AnalyticsProvider></UserSync></ApiProvider></QueryClientProvider></AuthProvider></PostHogProvider></UnleashProvider>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
export default ContextProvider;
type ContextConfig = {
  auth?: Partial<IAuthContext>;
  tasks?: Partial<ITaskContext>;
  alertBanner?: Partial<IAlertBannerContext<AlertBannerType>>;
  analytics?: AnalyticsContextType;
};
type ConfigurableProps = {
  children: React.ReactNode;
  config?: ContextConfig;
};

// used for tests
export const ConfigurableContextProvider: FC<ConfigurableProps> = t0 => {
  const $ = _c(29);
  const {
    children,
    config: customConfig
  } = t0;
  let t1;
  if ($[0] !== customConfig) {
    t1 = customConfig || {};
    $[0] = customConfig;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    auth: t2,
    tasks: t3,
    alertBanner: t4,
    analytics: t5
  } = t1;
  let t6;
  if ($[2] !== t2) {
    t6 = t2 === undefined ? {} : t2;
    $[2] = t2;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const authConfig = t6;
  let t7;
  if ($[4] !== t3) {
    t7 = t3 === undefined ? {} : t3;
    $[4] = t3;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const tasksConfig = t7;
  let t8;
  if ($[6] !== t4) {
    t8 = t4 === undefined ? {} : t4;
    $[6] = t4;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const alertBannerConfig = t8;
  const analyticsConfig = t5 === undefined ? defaultAnalyticsContext : t5;
  let t9;
  if ($[8] !== authConfig) {
    t9 = {
      ...defaultAuthContext,
      ...authConfig
    };
    $[8] = authConfig;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = analyticsConfig?.app || null;
  let t11;
  if ($[10] !== alertBannerConfig) {
    t11 = {
      ...(defaultAlertBannerContext as IAlertBannerContext<AlertBannerType>),
      ...alertBannerConfig
    };
    $[10] = alertBannerConfig;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      api: new Api({
        axios: {
          getAxiosInstance: createApi,
          factsServiceBaseUrl: getBaseUrl(config.factsHost),
          modellingServiceBaseUrl: getBaseUrl(config.modellingHost),
          usersServiceBaseUrl: getBaseUrl(config.usersHost),
          aiServiceBaseUrl: getBaseUrl(config.aiHost)
        }
      })
    };
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== tasksConfig) {
    t13 = {
      ...defaultTaskContext,
      ...tasksConfig
    };
    $[13] = tasksConfig;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== children) {
    t14 = <AntdThemeProvider isClientApp={true}><ThemeProvider theme={lightTheme}><ModalProvider>{children}</ModalProvider></ThemeProvider></AntdThemeProvider>;
    $[15] = children;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t13 || $[18] !== t14) {
    t15 = <QueryClientProvider><ApiContext.Provider value={t12}><TaskContext.Provider value={t13}>{t14}</TaskContext.Provider></ApiContext.Provider></QueryClientProvider>;
    $[17] = t13;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t11 || $[21] !== t15) {
    t16 = <TypedAlertBannerContext.Provider value={t11}>{t15}</TypedAlertBannerContext.Provider>;
    $[20] = t11;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t10 || $[24] !== t16) {
    t17 = <AnalyticsContext.Provider value={t10}>{t16}</AnalyticsContext.Provider>;
    $[23] = t10;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t17 || $[27] !== t9) {
    t18 = <AuthContext.Provider value={t9}>{t17}</AuthContext.Provider>;
    $[26] = t17;
    $[27] = t9;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  return t18;
};